import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { cn, fmtDate, fmtEur, euro } from '../../lib/utils';
import { toast } from '../../stores/toast.store';
import { useActionStreamStore } from '../../stores/actionStream.store';
import { useDocActions } from '../invoices/useDocActions';
import { useAdminActions } from '../admin/useAdminActions';
import { isoDay } from '../admin/adminApi';
import { Icon, type IconName } from '../../components/ui/Icon';
import type { ActionItem, ActionPrimaryKind } from './useActionStream';
import './oggi.css';

/**
 * ActionItemRow — riga dello stream "Oggi".
 *
 * Anatomia: rail severità (3px) · icon-tile sm · titolo+sub · importo ·
 * cluster azioni (in overlay, visibile su hover/focus-within; sul telefono,
 * dove l'hover non esiste, scende su una riga sua — features/oggi/oggi.css).
 *
 * Wiring delle azioni — SOLO mutation esistenti, nessun endpoint nuovo:
 *   incassa / sollecita / reinvia → useDocActions (condivise con Contabilità)
 *   risolvi                       → POST /notifications/alerts/:id/resolve
 *   done su notifica (FYI)        → POST /notifications/:id/read + archiveFyi
 *   completa (task)               → PATCH /tasks/:id { status: DONE } + Annulla che riapre
 *   incassa-rata / conferma       → useAdminActions.markPaid (Amministrazione): oggi via
 *                                   bonifico, oppure con il movimento Qonto proposto;
 *                                   l'Annulla del toast (unmark) rimette la riga
 *
 * Rimozione ottimistica: la riga esce subito (transizione 180ms gestita dal
 * parent via hide/unhide) e su errore fa rollback. Eccezione deliberata:
 * sollecita/reinvia hanno una confirm interna alla mutation, quindi la riga
 * esce solo a invio avvenuto (uscire prima della conferma sembrerebbe un bug);
 * lì l'uscita è solo locale (l'item non si chiude lato server) e il toast
 * "Fatto — Annulla" la ripristina davvero. Incassa/risolvi/segna-letta non
 * hanno endpoint inverso: niente Annulla, solo rollback su errore.
 *
 * Tastiera (gestita qui, la riga è il target del roving tabindex):
 *   Invio = primary · E = done · H = menu snooze · O = apri entità · Esc chiude il menu.
 */

export interface ActionItemRowProps {
  item: ActionItem;
  /** Roving tabindex: solo la riga attiva è raggiungibile con Tab. */
  active: boolean;
  /** Uscita animata in corso (rimozione ottimistica). */
  leaving: boolean;
  /** La riga ha preso il focus → diventa quella attiva. */
  onActivate: (key: string) => void;
  /** Avvia l'uscita animata; `after` parte a transizione conclusa. */
  hide: (key: string, after?: () => void) => void;
  /** Rollback: la riga torna visibile (errore mutation o Annulla). */
  unhide: (key: string) => void;
}

const PRIMARY_ICON: Record<ActionPrimaryKind, IconName> = {
  sollecita: 'bell',
  incassa: 'check',
  reinvia: 'send',
  risolvi: 'check',
  apri: 'arrowRight',
  'copia-link': 'link',
  completa: 'check',
  'incassa-rata': 'check',
  'conferma-incasso': 'check',
  riprova: 'refresh',
};

/** Opzioni snooze del menu H: quelle già passate (es. "Stasera" dopo le 18) spariscono. */
const SNOOZE_OPTIONS: Array<{ label: string; until: () => Date }> = [
  { label: 'Stasera 18:00', until: () => { const d = new Date(); d.setHours(18, 0, 0, 0); return d; } },
  { label: 'Domani 9:00', until: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
  { label: 'Lunedì 9:00', until: () => {
    const d = new Date();
    d.setDate(d.getDate() + (((1 - d.getDay() + 7) % 7) || 7)); // prossimo lunedì (mai oggi)
    d.setHours(9, 0, 0, 0);
    return d;
  } },
];

export function ActionItemRow({ item, active, leaving, onActivate, hide, unhide }: ActionItemRowProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { markInvoicePaid, sendInvoiceReminder, resendQuote } = useDocActions();
  const { markPaid: markRataPaid, syncQonto, syncFic } = useAdminActions();
  const snooze = useActionStreamStore((s) => s.snooze);
  const wake = useActionStreamStore((s) => s.wake);
  const archiveFyi = useActionStreamStore((s) => s.archiveFyi);
  const [menuOpen, setMenuOpen] = useState(false);

  // Risolvi alert di progetto — stessa mutation di Topbar/Dashboard
  const resolveAlert = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/alerts/${id}/resolve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
  // Segna notifica come letta — stesso endpoint della campanella in Topbar
  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Completa task: chiude e offre Annulla (riporta allo stato di prima)
  const completeTask = useMutation({
    mutationFn: (id: string) => api.patch(`/tasks/${id}`, { status: 'DONE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tasks'] }); qc.invalidateQueries({ queryKey: ['project'] }); },
  });

  const busy = markInvoicePaid.isPending || sendInvoiceReminder.isPending
    || resendQuote.isPending || resolveAlert.isPending || markRead.isPending || completeTask.isPending
    || markRataPaid.isPending || syncQonto.isPending || syncFic.isPending;

  /**
   * Riprova l'aggiornamento che non è riuscito. La riga non esce da sola: se
   * questa volta va bene sparisce al prossimo giro dell'overview, se no resta
   * lì con il nuovo errore. Anche in caso di errore si rileggono i dati, così
   * "Da dove vengono i numeri" dice il tentativo appena fatto.
   */
  const runRiprova = () => {
    const sync = item.actionData?.provider === 'QONTO' ? syncQonto : syncFic;
    sync.mutate(undefined, { onSettled: () => qc.invalidateQueries({ queryKey: ['admin'] }) });
  };

  /** Incassa: uscita ottimistica subito; su errore rollback (il toast.alert lo mostra la mutation). */
  const runIncassa = () => {
    hide(item.key);
    markInvoicePaid.mutateAsync({ id: item.entity.id, total: item.amount ?? 0 })
      .catch(() => unhide(item.key));
  };

  /**
   * Incassa una rata (Amministrazione): oggi via bonifico, oppure — se lo stream
   * ha proposto un movimento Qonto — con quel movimento e la sua data. Uscita
   * ottimistica; l'Annulla del toast (unmark) rimette la riga al suo posto.
   */
  const runIncassaRata = (withTx: boolean) => {
    const d = item.actionData;
    if (!d?.paymentId) { navigate(item.entity.path); return; }
    const tx = withTx && d.transactionId ? d.transactionId : null;
    hide(item.key);
    markRataPaid.mutateAsync({
      paymentId: d.paymentId,
      amount: item.amount ?? 0,
      label: item.title,
      paidDate: tx && d.settledAt ? d.settledAt.slice(0, 10) : isoDay(),
      method: 'BONIFICO',
      bankTransactionId: tx,
      onUndone: () => unhide(item.key),
    }).catch(() => unhide(item.key));
  };

  /** Sollecita: confirm dentro la mutation → la riga esce solo a invio avvenuto (sent=true). */
  const runSollecita = () => {
    const d = item.actionData;
    if (!d?.clientId) { navigate(item.entity.path); return; } // difensivo: senza dati si apre il dettaglio
    sendInvoiceReminder.mutateAsync({ id: item.entity.id, clientId: d.clientId, number: d.number ?? null })
      .then((sent) => {
        if (!sent) return; // annullato dalla confirm
        hide(item.key);
        toast.undo('Fatto', { description: item.title, onUndo: () => unhide(item.key) });
      })
      .catch(() => {}); // toast.alert già mostrato dalla mutation
  };

  /** Reinvia preventivo: stesso pattern di runSollecita. */
  const runReinvia = () => {
    const d = item.actionData;
    if (!d?.clientId) { navigate(item.entity.path); return; }
    resendQuote.mutateAsync({ id: item.entity.id, clientId: d.clientId, number: d.number ?? null, title: d.title ?? null })
      .then((sent) => {
        if (!sent) return;
        hide(item.key);
        toast.undo('Fatto', { description: item.title, onUndo: () => unhide(item.key) });
      })
      .catch(() => {});
  };

  /** Risolvi alert: ottimistico (nessuna confirm), rollback su errore. */
  const runRisolvi = () => {
    const alertId = item.actionData?.alertId;
    if (!alertId) { navigate(item.entity.path); return; }
    hide(item.key);
    resolveAlert.mutateAsync(alertId)
      .then(() => toast.ok('Alert risolto'))
      .catch((e: any) => { unhide(item.key); toast.alert('Errore', e?.message ?? 'Riprova'); });
  };

  /** Done su FYI: segna letta + archivio definitivo (solo a mutation riuscita). */
  const runMarkRead = () => {
    const notifId = item.actionData?.notifId;
    if (!notifId) return;
    hide(item.key);
    markRead.mutateAsync(notifId)
      .then(() => archiveFyi([item.key]))
      .catch((e: any) => { unhide(item.key); toast.alert('Errore', e?.message ?? 'Riprova'); });
  };

  /** Completa task: uscita ottimistica; Annulla nel toast riapre e ripristina la riga. */
  const runCompleta = () => {
    const id = item.entity.id;
    const prevStatus = item.actionData?.taskStatus && item.actionData.taskStatus !== 'DONE' ? item.actionData.taskStatus : 'TODO';
    hide(item.key);
    completeTask.mutateAsync(id)
      .then(() => {
        toast.undo('Task chiuso', {
          description: item.title,
          onUndo: () => {
            api.patch(`/tasks/${id}`, { status: prevStatus })
              .then(() => { qc.invalidateQueries({ queryKey: ['tasks'] }); unhide(item.key); })
              .catch((e: any) => toast.alert('Riapertura non riuscita', e?.message ?? 'Riprova'));
          },
        });
      })
      .catch((e: any) => { unhide(item.key); toast.alert('Task non chiuso', e?.message ?? 'Riprova'); });
  };

  const runCopiaLink = () => {
    navigator.clipboard.writeText(window.location.origin + item.entity.path)
      .then(() => toast.ok('Link copiato'))
      .catch(() => toast.alert('Copia non riuscita'));
  };

  const runPrimary = () => {
    if (busy) return;
    switch (item.primary?.kind) {
      case 'incassa': runIncassa(); break;
      case 'sollecita': runSollecita(); break;
      case 'reinvia': runReinvia(); break;
      case 'risolvi': runRisolvi(); break;
      case 'completa': runCompleta(); break;
      case 'incassa-rata': runIncassaRata(false); break;
      case 'conferma-incasso': runIncassaRata(true); break;
      case 'copia-link': runCopiaLink(); break;
      case 'riprova': runRiprova(); break;
      case 'apri':
      default: navigate(item.entity.path); // senza primary, Invio apre il dettaglio
    }
  };

  /** Done (E / check) = la mutation che chiude davvero l'item. */
  const runDone = () => {
    if (!item.canDone || busy) return;
    if (item.kind === 'notification') runMarkRead();
    else if (item.kind === 'project-alert') runRisolvi();
    else if (item.kind === 'invoice-overdue' || item.kind === 'invoice-due') runIncassa();
    else if (item.kind === 'task-overdue' || item.kind === 'task-due') runCompleta();
    else if (item.kind === 'rata-overdue' || item.kind === 'rata-due' || item.kind === 'to-review') runIncassaRata(item.primary?.kind === 'conferma-incasso');
  };

  const runSnooze = (until: Date, label: string) => {
    setMenuOpen(false);
    let shown = label;
    if (item.band === 0) {
      // band 0: lo store clampa a domani 09:00 — il toast dice la verità
      const cap = new Date();
      cap.setDate(cap.getDate() + 1);
      cap.setHours(9, 0, 0, 0);
      if (until.getTime() > cap.getTime()) shown = 'Domani 9:00 (limite critici)';
    }
    // lo snooze parte a uscita conclusa: così la riga esce con l'animazione
    // invece di sparire al primo recompute dello stream
    hide(item.key, () => snooze(item.key, until.toISOString(), item.band));
    toast.undo(`Posticipato a: ${shown}`, {
      description: item.title,
      onUndo: () => { wake(item.key); unhide(item.key); },
    });
  };

  const onRowKeyDown = (e: React.KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === 'Escape' && menuOpen) { e.stopPropagation(); setMenuOpen(false); return; }
    if (e.key === 'Enter') {
      if (e.target !== e.currentTarget) return; // Invio su un bottone interno: comportamento nativo
      e.preventDefault();
      runPrimary();
      return;
    }
    const k = e.key.toLowerCase();
    if (k === 'e' && item.canDone) { e.preventDefault(); runDone(); }
    else if (k === 'h' && item.canSnooze) { e.preventDefault(); setMenuOpen((v) => !v); }
    else if (k === 'o') { e.preventDefault(); navigate(item.entity.path); }
  };

  // Tile: rosso semantico solo per i critici, mute per le FYI, accento per il resto
  const tileColor = item.band === 0 ? 'var(--alert)' : item.band === 3 ? 'var(--mute)' : 'var(--accent)';
  // Il segno di spunta dice cosa registra: "Fatto" da solo non lo diceva.
  const doneLabel = item.kind === 'notification' ? 'Segna come letto'
    : item.kind === 'project-alert' ? 'Segna risolto'
    : item.kind === 'task-overdue' || item.kind === 'task-due' ? 'Segna completato'
    : 'Segna incassata';
  const snoozeOptions = SNOOZE_OPTIONS.filter((o) => o.until().getTime() > Date.now());

  return (
    <div
      className={cn('as-row', leaving && 'as-row--leaving')}
      data-as-row=""
      data-band={item.band}
      tabIndex={active ? 0 : -1}
      aria-label={item.title}
      onFocus={() => onActivate(item.key)}
      onClick={() => navigate(item.entity.path)}
      onKeyDown={onRowKeyDown}
    >
      <span className="as-rail" aria-hidden="true" />
      <div className="icon-tile sm" style={{ ['--c' as any]: tileColor }}>
        <Icon name={item.icon} size={14} />
      </div>
      <div className="as-text">
        <div className="as-title" title={item.title}>{item.title}</div>
        {/* Il tempo sta in parole ("in ritardo da 6 mesi"): la data esatta nel tooltip. */}
        <div className="as-sub" title={item.dueAt ? `Scadenza: ${fmtDate(item.dueAt)}` : undefined}>{item.sub}</div>
      </div>
      {/* La colonna di destra è l'importo, e basta. Qui c'era anche una chip
          con la scadenza, ma compariva solo sui task — che sono gli unici item
          senza importo — e diceva parola per parola quello che il sottotitolo
          dice già: "In ritardo di 1 g" a sinistra, "in ritardo di 1 g" a
          cinque pixel di distanza. In tutte le altre righe (fatture, rate,
          tasse, preventivi) il "quando" sta nel sottotitolo e a destra c'è il
          denaro: adesso la regola vale per tutte e sette. */}
      <div className="as-right">
        {item.amount != null && <span className="as-amount">{euro(item.amount)}</span>}
      </div>
      <div className="as-actions" onClick={(e) => e.stopPropagation()}>
        {item.primary && (
          <button
            className="btn btn-sm"
            disabled={busy}
            title={`${item.primary.label} (Invio)`}
            onClick={runPrimary}
          >
            <Icon name={PRIMARY_ICON[item.primary.kind]} size={10} /> {item.primary.label}
          </button>
        )}
        {item.canSnooze && (
          <div className="relative">
            <button
              className="btn btn-sm btn-ghost btn-icon"
              title="Posticipa (H)"
              aria-label="Posticipa"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <Icon name="clock" size={12} />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-[60]" onClick={() => setMenuOpen(false)} />
                <div className="dropdown right-0 mt-1 z-[61]" style={{ minWidth: 180 }} role="menu">
                  {snoozeOptions.map((o) => (
                    <button
                      key={o.label}
                      role="menuitem"
                      className="flex items-center gap-2.5 px-3 py-2.5 w-full text-left hover:bg-paper-2 transition text-[13px]"
                      onClick={() => runSnooze(o.until(), o.label)}
                    >
                      <Icon name="clock" size={12} className="mute" /> {o.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {item.canDone && (
          <button
            className="btn btn-sm btn-ghost btn-icon"
            disabled={busy}
            title={`${doneLabel} (E)`}
            aria-label={doneLabel}
            onClick={runDone}
          >
            <Icon name="check" size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
